import { createSnapshotValue as createMutable } from '../../lib/snapshot-value.ts'
import type { ProtoFile } from './orders.ts'

/**
 * Stock follows the product spec §12.2: physical, reserved, available = physical − reserved,
 * ordered (incoming, never stock) and a critical threshold. Quantities are in the base unit.
 */
export type WarehouseName = 'м. Львів' | 'м. Ралівка' | 'м. Стрий'

/** Spec §12.2: exactly three warehouses, each a flat list of locations. */
export const WAREHOUSES: WarehouseName[] = createMutable([] as WarehouseName[])

/** Mutable and reactive: «Налаштування» adds, renames and archives locations. */
export const LOCATIONS: Record<WarehouseName, string[]> = createMutable(
  {} as Record<WarehouseName, string[]>,
)

export const ARCHIVED_LOCATIONS: Record<WarehouseName, string[]> = createMutable(
  {} as Record<WarehouseName, string[]>,
)

/** Material categories, for filters and reports. Renaming one renames it on every material. */
export const MATERIAL_CATEGORIES: string[] = createMutable([] as string[])

export const ARCHIVED_CATEGORIES: string[] = createMutable([] as string[])

/**
 * Units a quantity can be counted in. The symbol is what records store, so it is set once; the
 * name and decimals can change. Purchase units (хлист, рулон) are set per material instead.
 */
export interface Unit {
  symbol: string
  name: string
  decimals: number
  archived: boolean
}

export const UNITS: Unit[] = createMutable([] as Unit[])

export interface StockLocation {
  warehouse: WarehouseName
  location: string
  physical: string
}

/** Spec §12.5: a purchase unit and how many base units it holds, set per material. */
export interface UnitConversion {
  unit: string
  factor: string
  note?: string
}

export interface ProtoMaterial {
  id: string
  name: string
  grade: string | null
  category: string
  unit: string
  /** Default location for receipts. Balances live in `locations`. */
  warehouse: WarehouseName
  location: string
  locations: StockLocation[]
  conversions: UnitConversion[]
  physical: string
  reserved: string
  ordered: string
  critical: string
  /** Last purchase price including VAT, in UAH per base unit. */
  unitCost: string
  supplier: string
}

export const MATERIALS: ProtoMaterial[] = createMutable([] as ProtoMaterial[])

export const materialById = (id: string): ProtoMaterial => {
  const material = MATERIALS.find((candidate) => candidate.id === id)
  if (!material) throw new Error(`Unknown prototype material ${id}`)
  return material
}

export interface BomLine {
  materialId: string
  /** Base-unit quantity for one finished product, before waste. */
  perUnit: string
  waste: string
  operation: string
}

/** Released technology cards. A product without one goes to engineering first. */
export const BOMS: Record<string, BomLine[]> = createMutable({} as Record<string, BomLine[]>)

/** An ordered delivery (spec §12.4): incoming, never stock until someone receives it. */
export interface ExpectedReceipt {
  id: string
  materialId: string
  quantity: string
  supplier: string
  expectedAt: string | null
  status: 'ordered' | 'in_transit' | 'partially_received' | 'received'
  /** UAH per base unit agreed with the supplier. */
  unitCost: string
  document: string | null
  received: string
  /** Supplier order number; one order can bring several materials. */
  supplyOrder: string
  buyerEntityId: string | null
  warehouse: WarehouseName
  location: string | null
  files: ProtoFile[]
  paymentRequestId: string | null
}

export const EXPECTED_RECEIPTS: ExpectedReceipt[] = createMutable([] as ExpectedReceipt[])

/**
 * Spec §12.8: an AI draft of an inbound waybill. Nothing here touches stock; the storekeeper
 * reviews every line and confirms, and only that posts receipts.
 */
export interface TtnLine {
  id: string
  /** The line as printed on the document. */
  text: string
  materialId: string | null
  quantity: string
  unit: string
  /** UAH per entered unit. */
  price: string
  /** 0 to 1: how sure the recognition is about the material match. */
  confidence: number
  skip: boolean
}

export interface TtnDraft {
  id: string
  fileName: string
  supplier: string
  document: string
  date: string
  lines: TtnLine[]
  status: 'review' | 'confirmed' | 'rejected'
  confirmedAt: string | null
}

/** What the recognition "reads" from any uploaded file in the prototype. */
export const SAMPLE_TTN = createMutable({
  supplier: '',
  document: '',
  date: '',
  lines: [] as {
    text: string
    materialId: string | null
    quantity: string
    unit: string
    price: string
    confidence: number
  }[],
})
